if (!customElements.get('shipping-rates-product')) {
    class ShippingRatesProduct extends HTMLElement {
        constructor() {
            super();
            this.form = null;
            this.ratesList = null;
            this.zipCode = null;
            this.button = null;
            this.message = null;
            this.useLocationButton = null;
            this.submitButton = null;
            this.place = null;
            this.placeText = null;
            this.location = this.getAttribute('data-location');
            this.variantID = null;
            this.originalCartItems = [];
            this.submitDebounceTimer = null;
            this.simulationCacheKey = 'bertolini-shipping-sim-cache-v1';
            this.simulationCacheTtlMs = 5 * 60 * 1000;
            this.initialized = false;
        }

        connectedCallback() {
            if (this.initialized) {
                return;
            }

            this.form = this.querySelector('[data-form="shipping-rates"]');
            this.ratesList = this.querySelector('[data-shipping-rates]');
            this.zipCode = this.querySelector('[data-zip-code]');
            this.button = this.querySelector('[data-form-show]');
            this.message = this.querySelector('[data-message]');
            this.useLocationButton = this.querySelector('.shipping-rates__use-location');
            this.submitButton = this.form?.querySelector('button[type="submit"]');
            this.place = this.querySelector('[data-shipping-place]');
            this.placeText = this.querySelector('[data-shipping-location]');

            if (!this.form || !this.ratesList || !this.message || !this.submitButton) {
                return;
            }

            this.prepareAnimatedElements();
            this.init();
            this.initialized = true;
        }

        getSavedAddress() {
            try {
                const saved = sessionStorage.getItem('bertolini-address');
                return saved ? JSON.parse(saved) : null;
            } catch (error) {
                console.error('Error reading saved address:', error);
                return null;
            }
        }

        normalizeCep(value) {
            return String(value || '').replace(/\D/g, '');
        }

        formatCep(value) {
            const digits = this.normalizeCep(value);
            if (digits.length !== 8) return value;
            return `${digits.slice(0, 5)}-${digits.slice(5)}`;
        }

        syncSavedAddress(data) {
            sessionStorage.setItem('bertolini-address', JSON.stringify(data));
            document.dispatchEvent(new CustomEvent('cep:updated', {
                detail: data
            }));
        }

        getSimulationCache() {
            try {
                const raw = sessionStorage.getItem(this.simulationCacheKey);
                return raw ? JSON.parse(raw) : {};
            } catch (error) {
                console.warn('Erro ao ler cache de simulação:', error);
                return {};
            }
        }

        readSimulationCache(key) {
            const cache = this.getSimulationCache();
            const entry = cache[key];
            if (!entry) return null;
            if (!entry.timestamp || (Date.now() - entry.timestamp) > this.simulationCacheTtlMs) {
                delete cache[key];
                sessionStorage.setItem(this.simulationCacheKey, JSON.stringify(cache));
                return null;
            }
            return entry;
        }

        writeSimulationCache(key, entry) {
            const cache = this.getSimulationCache();
            cache[key] = entry;
            sessionStorage.setItem(this.simulationCacheKey, JSON.stringify(cache));
        }

        buildCacheKey(variantId, quantity, zip) {
            return `${variantId}|${quantity}|${zip}`;
        }

        init() {
            const zipCodeInput = this.form.querySelector("#zip-code");

            this.form.addEventListener("submit", (event) => this.submit(event));
            zipCodeInput.addEventListener("input", (event) => this.maskZipCode(event));
            zipCodeInput.addEventListener("focus", () => this.hideAnimatedElement(this.message));
            zipCodeInput.addEventListener("click", () => this.hideAnimatedElement(this.message));
            if (this.button) this.button.addEventListener("click", () => this.reset());
            if (this.useLocationButton) {
                this.useLocationButton.addEventListener('click', () => this.useGeolocation());
            }
            this.prefillZipFromSession();
        }

        prepareAnimatedElements() {
            this.message.classList.remove('hidden');
            this.ratesList.classList.remove('hidden');
            this.hideAnimatedElement(this.message);
            this.hideAnimatedElement(this.ratesList);

            if (this.place) {
                this.place.classList.remove('hidden');
                this.hideAnimatedElement(this.place);
            }
        }

        reset() {
            this.hideAnimatedElement(this.ratesList);
            this.hideElement(this.zipCode);
            this.hideAnimatedElement(this.place);
            this.hideAnimatedElement(this.message);
        }

        async submit(event) {
            event.preventDefault();
            if (this.submitDebounceTimer) return;
            this.submitDebounceTimer = setTimeout(() => {
                this.submitDebounceTimer = null;
            }, 600);
            const button = this.form.querySelector('button[type="submit"]');
            button.disabled = true;
            button.textContent = 'Consultando...';
            this.hideAnimatedElement(this.message);
            this.hideAnimatedElement(this.ratesList);
            this.hideAnimatedElement(this.place);

            let restoreCart = false;

            try {
                const zip = this.form.querySelector('input[name="zip-code"]').value.replace(/\D/g, '');
                
                if (zip.length !== 8) {
                    throw new Error('CEP deve conter 8 dígitos. Verifique e tente novamente.');
                }

                let cacheKey = null;
                let variantId = null;
                let desiredQuantity = 1;

                // PDP - usa apenas o produto atual para simulação
                if (this.location === 'PDP') {
                    variantId = document.querySelector('input[name="id"]').value;
                    if (variantId) {
                        cacheKey = this.buildCacheKey(variantId, desiredQuantity, zip);
                        const cached = this.readSimulationCache(cacheKey);
                        if (cached && cached.rates) {
                            this.displayShippingRates(cached.rates);
                            this.updateButton(cached.cep || zip);
                            this.setLocationLabel(cached.city, cached.state);
                            return;
                        }
                    }

                    await this.saveCurrentCart();

                    if (this.cartMatchesSimulation(variantId, desiredQuantity)) {
                        restoreCart = false;
                    } else {
                        if (this.originalCartItems.length) {
                            await this.clearCart();
                        }
                        restoreCart = true;
                        await this.simulationCart(variantId, desiredQuantity);
                    }
                }

                const savedAddress = this.getSavedAddress();
                const savedCep = this.normalizeCep(savedAddress?.cep);

                let state, cep, city;
                if (savedAddress && savedCep === zip) {
                    state = savedAddress.state || savedAddress.uf || '';
                    cep = savedAddress.cep;
                    city = savedAddress.city;
                } else {
                    const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${zip}`);
                    if (!response.ok) {
                        if (response.status === 504) {
                            throw new Error('CEP inválido. Verifique o número e tente novamente.');
                        }
                        throw new Error('CEP inválido. Verifique o número e tente novamente.');
                    }
                    const data = await response.json();
                    state = data.state;
                    cep = data.cep;
                    city = data.city;

                    const addressData = {
                        cep: this.formatCep(zip),
                        city: city || '',
                        state: state || '',
                        neighborhood: data.neighborhood || '',
                        street: data.street || ''
                    };
                    this.syncSavedAddress(addressData);
                }

                const shippingRates = await this.fetchShippingRatesFast(state, cep);

                if (this.location === 'PDP' && cacheKey) {
                    this.writeSimulationCache(cacheKey, {
                        timestamp: Date.now(),
                        rates: shippingRates,
                        cep,
                        city,
                        state
                    });
                }

                this.displayShippingRates(shippingRates);
                this.updateButton(cep);
                this.setLocationLabel(city, state);

            } catch (error) {
                console.warn(error);
                let errorMessage = error.message || 'Erro ao consultar o frete.';
                
                if (errorMessage === 'Failed to fetch') {
                    errorMessage = 'CEP inválido. Verifique o número e tente novamente.';
                }
                
                this.displayError(errorMessage);
            } finally {
                if (restoreCart && this.location === 'PDP') await this.restoreOriginalCart();
                button.disabled = false;
                button.textContent = 'OK';
            }
        }

        async saveCurrentCart() {
            const res = await fetch('/cart.js');
            const data = await res.json();
            this.originalCartItems = data.items || [];
        }

        async clearCart() {
            await fetch('/cart/clear.js', { method: 'POST' });
        }

        async restoreOriginalCart() {
            await this.clearCart();
            if (!this.originalCartItems.length) return;
            const items = this.originalCartItems.map(i => ({ id: i.variant_id, quantity: i.quantity, properties: i.properties || {} }));
            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });
        }

        async simulationCart(variantId, quantity = 1) {
            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: variantId, quantity }] })
            });
        }

        cartMatchesSimulation(variantId, quantity) {
            if (this.originalCartItems.length !== 1) return false;
            const item = this.originalCartItems[0];
            const hasProperties = item.properties && Object.keys(item.properties).length > 0;

            return String(item.variant_id) === String(variantId)
                && item.quantity === quantity
                && !hasProperties;
        }

        // Versão simplificada - endpoint direto de shipping rates
        async fetchShippingRatesFast(province, zip) {
            const queryParams = new URLSearchParams({
                'shipping_address[zip]': zip,
                'shipping_address[country]': 'BR',
                'shipping_address[province]': province
            });

            const url = `/cart/shipping_rates.json?${queryParams.toString()}`;
            const response = await fetch(url);

            if (!response.ok || response.status === 204) {
                throw new Error('Não conseguimos consultar o frete. Tente novamente.');
            }

            const rates = await response.json();
            if (!rates?.shipping_rates?.length) {
                throw new Error('Não conseguimos consultar o frete. Tente novamente.');
            }

            return rates;
        }

        async fetchShippingRatesFast(province, zip) {
            const queryParams = new URLSearchParams({
                'shipping_address[zip]': zip,
                'shipping_address[country]': 'BR',
                'shipping_address[province]': province
            });

            const url = `/cart/shipping_rates.json?${queryParams.toString()}`;
            let lastError = new Error('Não conseguimos consultar o frete. Tente novamente.');

            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    const response = await fetch(url);

                    if (!response.ok || response.status === 204) {
                        throw new Error('Não conseguimos consultar o frete. Tente novamente.');
                    }

                    const rates = await response.json();
                    if (!rates?.shipping_rates?.length) {
                        throw new Error('Não conseguimos consultar o frete. Tente novamente.');
                    }

                    return rates;
                } catch (error) {
                    lastError = error;

                    if (attempt === 2) {
                        throw lastError;
                    }

                    await this.delay(400);
                }
            }

            throw lastError;
        }

        updateButton(zip) {
            if (!this.zipCode) return;
            this.zipCode.querySelector("span").textContent = zip;
            this.showElement(this.zipCode);
        }

        displayShippingRates(data) {
            const rates = data.shipping_rates || [];
            if (!rates.length) throw new Error('Nenhum método de envio disponível.');
            
            // Expande as rates para incluir "Entrega agendada" quando houver "Normal"
            const expandedRates = [];
            rates.forEach(rate => {
                expandedRates.push(rate);
                
                // Se for entrega Normal, adiciona também a Entrega agendada
                if (rate.name && rate.name.toLowerCase().includes('normal')) {
                    expandedRates.push({
                        ...rate,
                        name: 'Entrega agendada',
                        _isScheduled: true // Flag para identificar
                    });
                }
            });
            
            this.ratesList.innerHTML = expandedRates.map(rate => {
                const hasDeadline = Boolean(rate.delivery_days?.[0]);
                
                // Se for Entrega agendada, usa sempre a descrição específica
                const deadlineText = rate._isScheduled
                    ? 'Confira a disponibilidade na finalização de pedido'
                    : (hasDeadline
                        ? `Em até ${rate.delivery_days[0]} dias úteis`
                        : 'Confira a disponibilidade no checkout');
                
                const isPaid = parseFloat(rate.price) > 0;
                const priceText = isPaid ? `R$ ${rate.price.toString().replace('.', ',')}` : 'GRÁTIS';

                return `
                <li class="shipping-rate">
                    <div class="shipping-rate__info">
                        <span class="shipping-rate__title">${rate.name}</span>
                        <span class="shipping-rate__desc">${deadlineText}</span>
                    </div>
                    <div class="shipping-rate__price${isPaid ? '' : ' is-free'}">${priceText}</div>
                </li>
                `;
            }).join('');
            this.showAnimatedElement(this.ratesList);
        }

        maskZipCode(e) {
            let v = e.target.value.replace(/\D/g, '');
            if (v.length > 5) v = v.replace(/^(\d{5})(\d{1,3}).*/, '$1-$2');
            e.target.value = v;
        }

        delay(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        }

        displayError(msg) {
            this.message.textContent = msg;
            this.message.style.color = '#3e3e3d';
            this.showAnimatedElement(this.message);
        }

        prefillZipFromSession() {
            const input = this.form.querySelector('#zip-code');
            if (!input) return;

            const savedAddress = this.getSavedAddress();
            if (!savedAddress) return;

            try {
                if (savedAddress?.cep) {
                    input.value = savedAddress.cep;
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                }
                if (savedAddress?.city && savedAddress?.state) {
                    this.setLocationLabel(savedAddress.city, savedAddress.state);
                }
            } catch (error) {
                console.warn('Erro ao ler CEP salvo:', error);
            }
        }

        setLocationLabel(city, state) {
            if (!this.place || !this.placeText) return;
            if (!city || !state) {
                this.hideAnimatedElement(this.place);
                return;
            }

            this.placeText.textContent = `${city}, ${state}`;
            this.showAnimatedElement(this.place);
        }

        async useGeolocation() {
            if (!navigator.geolocation) {
                this.displayError('Geolocalização não disponível no seu navegador.');
                return;
            }

            const input = this.form.querySelector('#zip-code');
            if (!input) return;

            this.useLocationButton.disabled = true;
            const originalText = this.useLocationButton.textContent;
            this.useLocationButton.textContent = 'Obtendo localização...';
            this.setSubmitLoading(true);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;

                    fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
                        { headers: { 'User-Agent': 'BertoliniStore/1.0' } }
                    )
                        .then(response => response.json())
                        .then(data => {
                            const postcode = data.address?.postcode;
                            if (!postcode) throw new Error('CEP não encontrado para esta localização');

                            const cleanCEP = postcode.replace(/\D/g, '');
                            if (cleanCEP.length !== 8) {
                                throw new Error('CEP inválido obtido da localização');
                            }

                            const formattedCEP = `${cleanCEP.slice(0, 5)}-${cleanCEP.slice(5)}`;
                            input.value = formattedCEP;
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                            this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        })
                        .catch(error => {
                            console.warn('Erro no reverse geocoding:', error);
                            this.displayError('Não foi possível obter o CEP da sua localização. Digite manualmente.');
                        })
                        .finally(() => {
                            this.useLocationButton.disabled = false;
                            this.useLocationButton.textContent = originalText;
                            this.setSubmitLoading(false);
                        });
                },
                (error) => {
                    console.warn('Erro ao obter localização:', error);
                    let errorMessage = 'Não foi possível obter sua localização.';

                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = 'Permissão negada. Habilite a localização no navegador.';
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = 'Localização indisponível no momento';
                            break;
                        case error.TIMEOUT:
                            errorMessage = 'Tempo esgotado ao obter localização.';
                            break;
                        default:
                            break;
                    }

                    this.displayError(errorMessage);
                    this.useLocationButton.disabled = false;
                    this.useLocationButton.textContent = originalText;
                    this.setSubmitLoading(false);
                }
            );
        }

        setSubmitLoading(isLoading) {
            if (!this.submitButton) return;
            if (isLoading) {
                this.submitButton.disabled = true;
                this.submitButton.classList.add('is-loading');
                this.submitButton.dataset.originalText = this.submitButton.textContent;
                this.submitButton.textContent = 'OK';
                return;
            }

            this.submitButton.disabled = false;
            this.submitButton.classList.remove('is-loading');
            if (this.submitButton.dataset.originalText) {
                this.submitButton.textContent = this.submitButton.dataset.originalText;
                delete this.submitButton.dataset.originalText;
            }
        }

        showAnimatedElement(el) {
            el?.classList.add('is-visible');
            el?.setAttribute('aria-hidden', 'false');
        }

        hideAnimatedElement(el) {
            el?.classList.remove('is-visible');
            el?.setAttribute('aria-hidden', 'true');
        }

        showElement(el) { el?.classList.remove('hidden'); }
        hideElement(el) { el?.classList.add('hidden'); }
    }

    customElements.define('shipping-rates-product', ShippingRatesProduct);
}

